/**
 * web/src/hooks/mutations/useUpdateRole.ts
 *
 * PATCH /api/admin/users/:id/role — admin-only role change.
 *
 * Optimistically patches the user row in the qk.adminAccess() cache so the
 * dropdown reflects the new role immediately. Rolls back on error.
 */
import { patchUserRole } from '../../api/access';
import { useOptimisticMatrixMutation } from './_optimistic';

/** Body for the mutation. */
export interface UpdateRoleBody {
  userId: string;
  role: 'sales_ops' | 'content' | 'operations' | 'admin';
}

/**
 * React Query mutation hook for changing a user's role. Uses the shared
 * admin-matrix optimistic wrapper (F4): patches the user row immediately,
 * rolls back on error.
 *
 * @returns React Query mutation handle
 * @sideEffects HTTP PATCH, optimistic cache mutation, toast on outcome
 * @idempotent yes — same role lands at the same final state
 */
export function useUpdateRole() {
  return useOptimisticMatrixMutation<UpdateRoleBody>(
    (body) => patchUserRole(body.userId, body.role),
    (prev, body) => ({
      ...prev,
      users: prev.users.map((u) => (u.id === body.userId ? { ...u, role: body.role } : u)),
    }),
    { errorFallback: 'Failed to update role', successMessage: 'Role updated' },
  );
}
